import dayjs from 'dayjs';
import { IssueReport, IssueReportItem } from '../../../api/types';
import { escapeCSVField } from '../../../utils/csvFieldEscape';
import { formatAtStation, stationCalendarDay } from '../../../utils/stationTime';
import { AddressLineParts, formatAddressLine } from '../../../utils/addressLine';
import { issueReportStateLabel } from './issueReportStateLabel';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface IssueReportCsvRange {
    /** Calendar dates, `YYYY-MM-DD`, inclusive. Either may be empty. */
    dateFrom?: string;
    dateTo?: string;
}

/**
 * The reports whose creation day lies in the range. The day is the STATION's
 * calendar day (ADR-0019) — not the UTC date of the ISO string, which differs
 * for reports filed late in the evening, and not the operator's browser day.
 */
export const filterIssueReportsByCreatedDate = (
    reports: IssueReport[],
    { dateFrom, dateTo }: IssueReportCsvRange,
): IssueReport[] => {
    if (!dateFrom && !dateTo) return reports;
    return reports.filter((report) => {
        // A report without a known creation day cannot be placed in a range.
        const created = createdAtOf(report);
        if (!created) return false;
        const day = stationCalendarDay(created.toDate());
        if (dateFrom && day < dateFrom) return false;
        if (dateTo && day > dateTo) return false;
        return true;
    });
};

/**
 * A real creation time, or null. The list endpoint serialises a NULL
 * `created_at` as `new Date(null)` — 1970-01-01 — which must not reach a file
 * as if it were a date.
 */
const createdAtOf = (report: IssueReport): dayjs.Dayjs | null => {
    if (!report.createdAt) return null;
    const created = dayjs(report.createdAt);
    return created.isValid() && created.valueOf() > 0 ? created : null;
};

/** What the CSV needs to put a place on a report: the tenant's locations and stations. */
export interface IssueReportPlaces {
    locationsById: Map<string, { name?: string, address?: AddressLineParts | null }>;
    /** Station → its location, the detail page's first route to a report's place. */
    depotLocationIds: Map<string, string>;
}

/**
 * The report's address as one line, from its location through the shared
 * `formatAddressLine` — the list endpoint's own `address` string comes from the
 * backend's `formatAddress`, which drops the house number (QA #2439, defect D),
 * and exists only for reports on a station object. The location is found the
 * way the detail page finds it: the station's location first, then the
 * report's own `locationId`. The endpoint's string is the last resort.
 */
export const issueReportAddress = (report: IssueReport, places?: IssueReportPlaces): string => {
    const locationId = (report.depotId && places?.depotLocationIds.get(report.depotId))
        || report.locationId;
    const location = locationId ? places?.locationsById.get(locationId) : undefined;
    return formatAddressLine(location?.address) || report.address || '';
};

/**
 * An equipment item's name as the customer saw it on the check screen: its
 * configured label in the export's language, then German, then any, then the
 * key. Same order as the end-user app's `resolveLabel`
 * (`apps/end-user/src/modules/useOrder/equipmentRental/EquipmentCheckStep.tsx`).
 */
export const equipmentLabel = (item: IssueReportItem, language: string): string => {
    const labels = item.label_i18n;
    if (!labels || typeof labels !== 'object') return item.key;
    const lang = language.split('-')[0];
    return labels[lang] ?? labels.de ?? Object.values(labels)[0] ?? item.key;
};

const translatedOr = (t: Translate, key: string, fallback: string): string => {
    const label = t(key);
    return label && label !== key ? label : fallback;
};

/**
 * The CSV for a list of problem reports — `;`-separated, every cell escaped
 * (including the formula-injection guard: `message` is customer-typed text).
 *
 * The reporter's name and email are deliberately NOT exported (PO,
 * 2026-10-03): a file leaves our control, and the analysis this serves does
 * not need them. Adding them later is a column, not a redesign.
 */
export const buildIssueReportCsv = (
    reports: IssueReport[],
    t: Translate,
    places?: IssueReportPlaces,
    language = 'de',
): string => {
    const missing = t('common.missing');
    const notApplicable = t('common.not_applicable');
    const value = (v: string | number | null | undefined, empty = missing): string =>
        v === null || v === undefined || v === '' ? empty : String(v);

    const headers = [
        'report_id', 'created_at', 'status', 'equipment_check', 'booking_number', 'address',
        'locker', 'service', 'issue_type', 'created_by', 'message', 'flagged_items',
        'comments', 'archived_at', 'archive_reason',
    ].map((key) => t(`issue_reports.csv.headers.${key}`));

    const itemLabel = (item: IssueReportItem): string =>
        equipmentLabel(item, language) + ': '
        + translatedOr(t, `issue_reports.csv.item_kind.${item.issueKind}`, item.issueKind);

    const rows = reports.map((report) => {
        const items = Array.isArray(report.items) ? report.items : [];
        // Count real comments only: typed entries are system notes (the
        // report's own `creation` entry, a `booking_deleted` note).
        const comments = Array.isArray(report.comments)
            ? report.comments.filter((c) => !c?.type).length
            : 0;
        return [
            report.id,
            formatAtStation(createdAtOf(report)?.toDate(), 'yyyy-MM-dd HH:mm') || missing,
            issueReportStateLabel(report.state, t),
            report.status ? t(`issue_reports.customer_status.${report.status}`) : notApplicable,
            value(report.bookingNumber, notApplicable),
            // No place at all: the field does not apply. A place without an
            // address: it should be there and is not.
            value(issueReportAddress(report, places), report.locationId || report.depotId ? missing : notApplicable),
            value(report.door, notApplicable),
            // The label the detail page shows (`service_type_selector.*`), not the code.
            report.service ? translatedOr(t, `service_type_selector.${report.service}`, report.service) : notApplicable,
            report.issueType ? translatedOr(t, `issue_type.${report.issueType}`, report.issueType) : notApplicable,
            report.createdBy
                ? translatedOr(t, `issue_reports.csv.created_by.${report.createdBy}`, report.createdBy)
                : missing,
            value(report.message),
            items.length > 0
                ? items.map(itemLabel).join(', ')
                : notApplicable,
            String(comments),
            formatAtStation(report.archivedAt, 'yyyy-MM-dd HH:mm') || notApplicable,
            report.archiveReason
                ? translatedOr(t, `bookings.archive.dialog.reason_options.${report.archiveReason}`, report.archiveReason)
                : notApplicable,
        ];
    });

    return [headers, ...rows]
        .map((row) => row.map(escapeCSVField).join(';'))
        .join('\n');
};

/** `<tenant>-problemberichte-<from>_<to>-<today>.csv`, the range only when one was chosen. */
export const issueReportCsvFileName = (
    tenant: string,
    language: string,
    { dateFrom, dateTo }: IssueReportCsvRange,
): string => {
    const parts = [
        tenant.toLowerCase().replace(/\s+/g, '-'),
        language === 'de' ? 'problemberichte' : 'issue-reports',
    ];
    if (dateFrom || dateTo) parts.push(`${dateFrom || 'start'}_${dateTo || 'end'}`);
    parts.push(stationCalendarDay(new Date()));
    return parts.join('-') + '.csv';
};
